import React from 'react';
import {
  BookOpen, LogIn, FilePlus2, Wrench, FileText, Mail, Search,
  Pencil, Trash2, Lightbulb, MousePointerClick, PlusCircle, Library, KeyRound,
} from 'lucide-react';

// A plain-language user guide for the BABS quoting app, written for people who are not
// comfortable with computers. Big text, short sentences, one action per step, and simple
// drawn visuals (numbered badges + icons) instead of screenshots.

// ---- Small building blocks ------------------------------------------------------

// A numbered step with a short instruction.
const Step = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white text-base font-bold">{n}</span>
    <span className="text-lg text-gray-800 leading-relaxed pt-0.5">{children}</span>
  </li>
);

// A friendly tip / note box.
const Tip = ({ children }) => (
  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
    <Lightbulb className="h-6 w-6 text-amber-500 flex-shrink-0" />
    <p className="text-base text-amber-900 leading-relaxed">{children}</p>
  </div>
);

// One help section with a big heading and an icon.
const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
    <div className="flex items-center gap-3 mb-4">
      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-red-100 text-red-700 flex-shrink-0">
        <Icon className="h-7 w-7" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    {children}
  </section>
);

// A button in the "jump to" list at the top.
const TocItem = ({ targetId, icon: Icon, label }) => (
  <a
    href={`#${targetId}`}
    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-red-50 hover:border-red-200 transition-colors"
  >
    <Icon className="h-5 w-5 text-red-600 flex-shrink-0" />
    <span className="text-base font-medium text-gray-800">{label}</span>
  </a>
);

// ---- The page -------------------------------------------------------------------

const Help = () => {
  return (
    <div className="p-4">
      <div className="max-w-4xl mx-auto">
        {/* Title */}
        <div className="flex items-center gap-3 mb-2 mt-2">
          <BookOpen className="h-9 w-9 text-red-700" />
          <h1 className="text-3xl font-bold text-red-700">User Guide</h1>
        </div>
        <p className="text-lg text-gray-600 mb-6">
          A simple, step-by-step help book for using the BABS quoting program. Take your time —
          each task below is broken into small steps.
        </p>

        {/* Quick tips */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-8">
          <h2 className="text-xl font-bold text-red-800 mb-3">A few things to know first</h2>
          <ul className="space-y-2 text-lg text-gray-800">
            <li className="flex items-start gap-2"><MousePointerClick className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" /> The menu on the left side of the screen takes you to every part of the program.</li>
            <li className="flex items-start gap-2"><Lightbulb className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" /> If you ever feel lost, click <strong>Quotes Data</strong> on the left to go back to the main list.</li>
            <li className="flex items-start gap-2"><Lightbulb className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" /> Nothing is saved until you click a <strong>Save</strong>, <strong>Create Quote</strong>, or <strong>Update Quote</strong> button. You can take your time.</li>
          </ul>
        </div>

        {/* Jump to */}
        <h2 className="text-xl font-bold text-gray-900 mb-3">Jump to a topic</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <TocItem targetId="login" icon={LogIn} label="Signing in" />
          <TocItem targetId="password" icon={KeyRound} label="Changing your password" />
          <TocItem targetId="make-quote" icon={FilePlus2} label="Making a new quote" />
          <TocItem targetId="saw" icon={Wrench} label="Choosing or adding a saw" />
          <TocItem targetId="brochure" icon={FileText} label="Adding or editing a brochure" />
          <TocItem targetId="send" icon={Mail} label="Sending a quote by email" />
          <TocItem targetId="find" icon={Search} label="Finding a quote" />
          <TocItem targetId="update" icon={Pencil} label="Changing a quote" />
          <TocItem targetId="delete" icon={Trash2} label="Deleting a quote" />
        </div>

        {/* Signing in */}
        <Section id="login" icon={LogIn} title="Signing in">
          <ol className="space-y-4">
            <Step n={1}>Open the program in your web browser.</Step>
            <Step n={2}>Type your <strong>email address</strong> and your <strong>password</strong>.</Step>
            <Step n={3}>Click the <strong>Sign In</strong> button.</Step>
            <Step n={4}>You will land on the list of quotes. You are ready to go.</Step>
          </ol>
          <Tip>If your password does not work, use the “Forgot Password” link, or ask the office to reset it for you.</Tip>
        </Section>

        {/* Changing your password */}
        <Section id="password" icon={KeyRound} title="Changing your password">
          <ol className="space-y-4">
            <Step n={1}>Look in the <strong>top-right corner</strong> of the screen and click your <strong>name or picture</strong>.</Step>
            <Step n={2}>In the little menu that drops down, click <strong>Account Settings &amp; Security</strong>.</Step>
            <Step n={3}>Find the <strong>Password</strong> area and click the <strong>Change Password</strong> button.</Step>
            <Step n={4}>Type your <strong>new password</strong>. It must be at least <strong>6 characters</strong> long.</Step>
            <Step n={5}>To make sure you typed it correctly, click the small <strong>eye</strong> icon to show the letters.</Step>
            <Step n={6}>Click the <strong>Change Password</strong> button to save. You will see a message that it worked.</Step>
          </ol>
          <Tip>Pick something you will remember but others cannot guess. Write it down somewhere safe if that helps.</Tip>
        </Section>

        {/* Making a quote */}
        <Section id="make-quote" icon={FilePlus2} title="Making a new quote">
          <ol className="space-y-4">
            <Step n={1}>On the left menu, click <strong>Create New Quote</strong>.</Step>
            <Step n={2}>Choose the <strong>customer</strong> this quote is for from the customer box.</Step>
            <Step n={3}>Pick the <strong>saw</strong> and conveyor options (see “Choosing or adding a saw” below).</Step>
            <Step n={4}>Fill in the quote details and prices on the page as needed.</Step>
            <Step n={5}>When everything looks right, click the <strong>Create Quote</strong> button.</Step>
            <Step n={6}>You will see a message that the quote was saved. Done!</Step>
          </ol>
          <Tip>You do not have to finish all at once. Nothing is saved until you click <strong>Create Quote</strong>.</Tip>
        </Section>

        {/* Choosing or adding a saw */}
        <Section id="saw" icon={Wrench} title="Choosing or adding a saw">
          <p className="text-lg text-gray-800 mb-4">On the quote page, find the <strong>Saw &amp; Conveyor Details</strong> box.</p>
          <ol className="space-y-4">
            <Step n={1}>Click the <strong>Saw Brand &amp; Model</strong> drop-down.</Step>
            <Step n={2}>Click the saw you want from the list. Its details fill in by themselves.</Step>
            <Step n={3}>To remove a saw you picked, click the small <strong>Remove</strong> button next to it. A quote can be saved with no saw.</Step>
          </ol>
          <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4 mt-5">
            <PlusCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
            <p className="text-lg text-gray-800"><strong>Need a saw that is not in the list?</strong> Click <strong>➕ Add New Saw</strong> at the top of the drop-down, fill in the form, and click <strong>Create</strong>. Your new saw is saved and will stay in the list from now on.</p>
          </div>
        </Section>

        {/* Brochure */}
        <Section id="brochure" icon={FileText} title="Adding or editing a brochure">
          <p className="text-lg text-gray-800 mb-4">A brochure is the PDF picture sheet for a saw. You can attach one to a quote.</p>
          <ol className="space-y-4">
            <Step n={1}>In the <strong>Saw &amp; Conveyor Details</strong> box, find <strong>Brochure</strong> and click <strong>Choose File To Upload</strong>.</Step>
            <Step n={2}>A small window asks where the PDF is coming from. You have two choices:</Step>
          </ol>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2"><FileText className="h-5 w-5 text-red-600" /><span className="font-semibold text-gray-900">Select from Computer</span></div>
              <p className="text-base text-gray-700">Pick a PDF saved on your own computer, then click <strong>Upload</strong>.</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2"><Library className="h-5 w-5 text-red-600" /><span className="font-semibold text-gray-900">Select from Cosen Library</span></div>
              <p className="text-base text-gray-700">Pick from the ready-made Cosen brochures. Type in the search box to find one quickly, then click it.</p>
            </div>
          </div>
          <ol className="space-y-4 mt-5" start={3}>
            <Step n={3}>A viewer opens showing the brochure. You can <strong>add text, draw, highlight, cover something with a white box, or add a picture</strong> using the buttons at the top.</Step>
            <Step n={4}>When it looks right, click <strong>Use this brochure</strong>. It is saved to this quote.</Step>
            <Step n={5}>The file name now shows as a red link. <strong>Click that link any time</strong> to open the brochure again and make more changes.</Step>
            <Step n={6}>To take the brochure off the quote, click <strong>Remove</strong>.</Step>
          </ol>
          <Tip>Your changes stay with <strong>this quote only</strong>. They do not change the saw for other quotes, and they do not change the original Cosen file. Each quote keeps its own copy.</Tip>
        </Section>

        {/* Sending a quote */}
        <Section id="send" icon={Mail} title="Sending a quote by email">
          <ol className="space-y-4">
            <Step n={1}>Open the quote (from <strong>Quotes Data</strong>, click the quote, or use the <strong>Send Quote</strong> action).</Step>
            <Step n={2}>Check the <strong>email address</strong> it is going to. You can add more addresses if needed.</Step>
            <Step n={3}>If this quote has a <strong>brochure</strong>, you will see “Brochure will be attached.”</Step>
            <Step n={4}>Click <strong>Send</strong>. The customer gets the quote PDF (and the brochure, if there is one).</Step>
          </ol>
        </Section>

        {/* Finding a quote */}
        <Section id="find" icon={Search} title="Finding a quote">
          <ol className="space-y-4">
            <Step n={1}>Click <strong>Quotes Data</strong> on the left menu to see all quotes.</Step>
            <Step n={2}>Use the search box to look by customer name or quote number.</Step>
            <Step n={3}>Click any column heading (like <strong>Date Created</strong>) to sort the list.</Step>
            <Step n={4}>Click a quote to open it.</Step>
          </ol>
          <Tip>The newest quotes show at the top by default, so the quote you just made is easy to find.</Tip>
        </Section>

        {/* Updating a quote */}
        <Section id="update" icon={Pencil} title="Changing a quote">
          <ol className="space-y-4">
            <Step n={1}>Find the quote in <strong>Quotes Data</strong>.</Step>
            <Step n={2}>Click the <strong>three dots</strong> on the right of that row, then click <strong>Update Quote</strong>.</Step>
            <Step n={3}>Make your changes on the page.</Step>
            <Step n={4}>Click the <strong>Update Quote</strong> button to save.</Step>
          </ol>
          <Tip>Only the newest version of a quote can be changed. If you do not see “Update Quote,” you are looking at an older version.</Tip>
        </Section>

        {/* Deleting a quote */}
        <Section id="delete" icon={Trash2} title="Deleting a quote">
          <ol className="space-y-4">
            <Step n={1}>Find the quote in <strong>Quotes Data</strong>.</Step>
            <Step n={2}>Click the <strong>three dots</strong> on the right of that row.</Step>
            <Step n={3}>Click <strong>Delete Quote</strong>.</Step>
            <Step n={4}>A box asks if you are sure. Click <strong>Delete</strong> to remove it, or <strong>Cancel</strong> to keep it.</Step>
          </ol>
          <Tip>Deleting cannot be undone, so only delete a quote you are sure you do not need.</Tip>
        </Section>

        <p className="text-center text-gray-500 text-base py-6">
          Still stuck? Call the office — someone will be glad to walk you through it.
        </p>
      </div>
    </div>
  );
};

export default Help;
